import {request} from '@/shared/api';
import type {FirmwareKind, FirmwareRelease} from '@/entities/firmware/types';

/**
 * GET /admin/firmware/releases?channel=&kind=&limit=1 — the build a terminal on this
 * track and channel would install right now, or `null` if nothing is published.
 *
 * The registry answers newest-published first, so the first row IS "latest".
 *
 * ⚠️ This route sits in the `system` Casbin domain (`firmware` resource), not in the
 * company's: the registry is vendor-global. A company owner without a platform role
 * gets `403` here — there is no company-scoped "latest release" route on the gateway
 * yet (CABINET-BACKEND-ASKS.md, §firmware). No `X-Company-Id`: the header would not
 * change the domain, and sending it would suggest otherwise to the next reader.
 */
export async function fetchLatestRelease(
  kind: FirmwareKind,
  channel = 'stable',
): Promise<FirmwareRelease | null> {
  const query = new URLSearchParams({channel, kind, limit: '1'}).toString();
  // The guide documents a bare array; the gateway actually wraps it as
  // `{releases: [...]}` (the admin console reads it that way). Reading only one
  // shape made the first row `undefined` — "could not compare" forever.
  const body = await request<
    FirmwareRelease[] | {releases?: FirmwareRelease[] | null} | null
  >(`/admin/firmware/releases?${query}`);
  const rows = Array.isArray(body) ? body : body?.releases;
  return rows?.[0] ?? null;
}
